import styled, { css } from 'styled-components';

import { Button } from '../button';

// Font styles
const crFont9 = css`
  font-family: 'Open Sans';
  font-size: 9px;
  font-style: normal;
  font-weight: 700;
  line-height: 11px;
`;

const crFont11 = css`
  font-family: 'Open Sans';
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 13px;
`;

// Custom scroll styles
const customSlimScroll = css`
  &::-webkit-scrollbar {
    width: 4px; /* Mostly for vertical scrollbars */
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.dataTable.scrollbarThumbColor};
    border-radius: 1px;
    &:hover {
      background: ${({ theme }) => theme.dataTable.scrollbarThumbHoverColor};
    }
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.dataTable.scrollbarTrackColor};
  }
`;

export const TableContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.dataTable.backgroundColor};
  border: 1px solid ${({ theme }) => theme.dataTable.borderColor};
  overflow: hidden;
  box-sizing: border-box;
  outline: none;
  position: relative;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.dataTable.rowSelectedBorderColor};
    outline-offset: -2px;
  }
`;

export const DataTableHeader = styled.div`
  background-color: ${({ theme }) => theme.dataTable.headerBackgroundColor};
  border-bottom: 1px solid ${({ theme }) => theme.dataTable.borderColor};
`;

export const TableWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  ${customSlimScroll}

  /* Row styling for data table */
  .data-table-row.even {
    background-color: ${({ theme }) => theme.dataTable.rowBackgroundColorEven};
  }

  .data-table-row.odd {
    background-color: ${({ theme }) => theme.dataTable.rowBackgroundColor};
  }

  .data-table-row:hover {
    background-color: ${({ theme }) => theme.dataTable.rowHoverBackgroundColor};
  }
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;

  th,
  td {
    box-sizing: border-box;
  }
`;

export const TableHeader = styled.thead`
  position: sticky;
  top: 0;
  z-index: 10;
`;

export const TableBody = styled.tbody`
  tr:nth-child(even) {
    background-color: ${({ theme }) => theme.dataTable.rowBackgroundColorEven};
  }
  tr:nth-child(odd) {
    background-color: ${({ theme }) => theme.dataTable.rowBackgroundColor};
  }
  tr:hover {
    background-color: ${({ theme }) => theme.dataTable.rowHoverBackgroundColor};
  }
  tr:has(td[data-row-selected='true'][data-highlight-selected='true']) {
    background-color: ${({ theme }) => theme.dataTable.rowSelectedBackgroundColor} !important;

    /* Apply border to first and last cells, and top/bottom borders to all cells */
    td:first-child {
      border-left: ${({ theme }) =>
        `1px solid ${theme.dataTable.rowSelectedBorderColor}`} !important;
      border-top: ${({ theme }) =>
        `1px solid ${theme.dataTable.rowSelectedBorderColor}`} !important;
      border-bottom: ${({ theme }) =>
        `1px solid ${theme.dataTable.rowSelectedBorderColor}`} !important;
      border-top-left-radius: 2px !important;
      border-bottom-left-radius: 2px !important;
    }

    td:last-child {
      border-right: ${({ theme }) =>
        `1px solid ${theme.dataTable.rowSelectedBorderColor}`} !important;
      border-top: ${({ theme }) =>
        `1px solid ${theme.dataTable.rowSelectedBorderColor}`} !important;
      border-bottom: ${({ theme }) =>
        `1px solid ${theme.dataTable.rowSelectedBorderColor}`} !important;
      border-top-right-radius: 2px !important;
      border-bottom-right-radius: 2px !important;
    }

    td:not(:first-child):not(:last-child) {
      border-top: ${({ theme }) =>
        `1px solid ${theme.dataTable.rowSelectedBorderColor}`} !important;
      border-bottom: ${({ theme }) =>
        `1px solid ${theme.dataTable.rowSelectedBorderColor}`} !important;
    }

    td {
      background-color: ${({ theme }) => theme.dataTable.rowSelectedBackgroundColor} !important;
    }

    &:hover {
      background-color: ${({ theme }) => theme.dataTable.rowSelectedBackgroundColor} !important;
    }
  }
  /* Highlighted row styling (for keyboard navigation) */
  tr:has(td[data-row-highlighted='true']) {
    background-color: ${({ theme }) => theme.dataTable.rowHoverBackgroundColor} !important;

    /* Apply border to first and last cells, and top/bottom borders to all cells */
    td:first-child {
      border-left: ${({ theme }) =>
        `1px solid ${theme.dataTable.rowSelectedBorderColor}`} !important;
      border-top: ${({ theme }) =>
        `1px solid ${theme.dataTable.rowSelectedBorderColor}`} !important;
      border-bottom: ${({ theme }) =>
        `1px solid ${theme.dataTable.rowSelectedBorderColor}`} !important;
      border-top-left-radius: 2px !important;
      border-bottom-left-radius: 2px !important;
    }

    td:last-child {
      border-right: ${({ theme }) =>
        `1px solid ${theme.dataTable.rowSelectedBorderColor}`} !important;
      border-top: ${({ theme }) =>
        `1px solid ${theme.dataTable.rowSelectedBorderColor}`} !important;
      border-bottom: ${({ theme }) =>
        `1px solid ${theme.dataTable.rowSelectedBorderColor}`} !important;
      border-top-right-radius: 2px !important;
      border-bottom-right-radius: 2px !important;
    }

    td:not(:first-child):not(:last-child) {
      border-top: ${({ theme }) =>
        `1px solid ${theme.dataTable.rowSelectedBorderColor}`} !important;
      border-bottom: ${({ theme }) =>
        `1px solid ${theme.dataTable.rowSelectedBorderColor}`} !important;
    }

    td {
      background-color: ${({ theme }) => theme.dataTable.rowHoverBackgroundColor} !important;
    }

    &:hover {
      background-color: ${({ theme }) => theme.dataTable.rowHoverBackgroundColor} !important;
    }
  }
`;

export const HeaderRow = styled.tr`
  background-color: ${({ theme }) => theme.dataTable.headerBackgroundColor};
`;

export const HeaderCell = styled.th<{
  width: number;
  isResizing?: boolean;
  borderedCell?: boolean;
  isDragging?: boolean;
  isDropTarget?: boolean;
  draggable?: boolean;
  dropPosition?: 'left' | 'right' | null;
}>`
  position: relative;
  width: ${({ width }) => width}px;
  min-width: ${({ width }) => width}px;
  max-width: ${({ width }) => width}px;
  height: 30px;
  padding: 0 12px;
  background-color: ${({ theme, isDragging }) =>
    isDragging ? theme.dataTable.rowHoverBackgroundColor : theme.dataTable.headerBackgroundColor};
  color: ${({ theme }) => theme.dataTable.headerTextColor};
  vertical-align: middle;
  border-right: ${({ theme, dropPosition, isDropTarget }) => {
    if (isDropTarget && dropPosition === 'right') {
      return `2px solid ${theme.dataTable.rowSelectedBorderColor}`;
    }
    return `1px solid ${theme.dataTable.rowBorderColor}`;
  }};
  border-left: ${({ theme, dropPosition, isDropTarget }) => {
    if (isDropTarget && dropPosition === 'left') {
      return `2px solid ${theme.dataTable.rowSelectedBorderColor}`;
    }
    return 'none';
  }};
  border-bottom: ${({ borderedCell, theme }) =>
    borderedCell === true ? `1px solid ${theme.dataTable.headerBorderColor}` : 'none'};
  user-select: none;
  cursor: ${({ isResizing }) => (isResizing ? 'col-resize' : 'default')};
  overflow: visible;
  text-overflow: ellipsis;
  white-space: nowrap;
  box-sizing: border-box;
  transition: background-color 0.2s ease, border-color 0.2s ease, border-width 0.2s ease;

  ${crFont9}
  text-transform: uppercase;
  &:last-child {
    border-right: ${({ theme, dropPosition, isDropTarget }) => {
      if (isDropTarget && dropPosition === 'right') {
        return `2px solid ${theme.dataTable.rowSelectedBorderColor}`;
      }
      return 'none';
    }};
  }
`;

export const SelectionHeaderCell = styled(HeaderCell)`
  position: sticky;
  left: 0;
  background-color: ${({ theme }) => theme.dataTable.headerBackgroundColor};
  text-align: center;
  z-index: 5;
  width: 50px;
`;

export const ActionsHeaderCell = styled(HeaderCell)`
  position: sticky;
  right: 0;
  background-color: ${({ theme }) => theme.dataTable.actionsColumnBackgroundColor};
  border-left: 2px solid ${({ theme }) => theme.dataTable.actionsColumnBorderColor};
  text-align: center;
  z-index: 4;
  width: 50px;
`;

export const DataCell = styled.td<{
  width: number;
  isClickable?: boolean;
  borderedCell?: boolean;
  align?: 'left' | 'center' | 'right';
}>`
  width: ${({ width }) => width}px;
  min-width: ${({ width }) => width}px;
  max-width: ${({ width }) => width}px;
  height: 40px;
  padding: 0 12px;
  color: ${({ theme }) => theme.dataTable.rowTextColor};
  font-size: 14px;
  text-align: ${({ align }) => align || 'left'};
  vertical-align: middle;
  border-right: ${({ borderedCell, theme }) =>
    borderedCell === true ? `1px solid ${theme.dataTable.rowBorderColor}` : 'none'};
  border-bottom: ${({ borderedCell, theme }) =>
    borderedCell === true ? `1px solid ${theme.dataTable.rowBorderColor}` : 'none'};
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
  position: relative;
  overflow: visible;
  box-sizing: border-box;

  ${crFont11}

  &:last-child {
    border-right: none;
  }
`;

export const CellContent = styled.div<{ width?: number | string }>`
  max-width: ${({ width }) =>
    width && typeof width === 'number' ? width - 24 : width}px; /* Subtract padding */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SelectionCell = styled(DataCell)`
  position: sticky;
  left: 0;
  background-color: inherit;
  text-align: center;
  padding: 0;
  width: 50px;
  z-index: 1;
`;

export const ActionsCell = styled(DataCell)`
  position: sticky;
  right: 0;
  background-color: inherit;
  border-left: 2px solid ${({ theme }) => theme.dataTable.actionsColumnBorderColor};
  text-align: center;
  padding: 0;
  width: 50px;
`;

export const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  right: -2px;
  width: 4px;
  height: 100%;
  background-color: transparent;
  cursor: col-resize;
  z-index: 6;

  &:hover {
    background-color: ${({ theme }) => theme.dataTable.resizeHandleHoverColor};
  }

  &:active {
    background-color: ${({ theme }) => theme.dataTable.resizeHandleColor};
  }
`;

export const SortButton = styled.button<{ 'data-sortable'?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: ${({ theme }) => theme.dataTable.sortIconColor};
  cursor: ${({ 'data-sortable': isSortable }) => (isSortable ? 'pointer' : 'default')};
  font-size: inherit;
  font-weight: inherit;
  text-transform: inherit;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: ${({ theme, 'data-sortable': isSortable }) =>
      isSortable ? theme.dataTable.sortIconActiveColor : theme.dataTable.sortIconColor};
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: default;
  }
`;

export const SortIcon = styled.span<{
  direction: 'asc' | 'desc' | 'none';
  'data-sortable'?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  opacity: ${({ direction }) => (direction === 'none' ? 0.3 : 1)};
  transform: ${({ direction }) => (direction === 'desc' ? 'rotate(180deg)' : 'none')};
  transition: all 0.2s ease;
  visibility: ${({ 'data-sortable': isSortable }) => (isSortable ? 'visible' : 'hidden')};
`;

export const FilterIcon = styled.button<{ isActive?: boolean }>`
  position: absolute;
  top: 0px;
  right: 0px;
  padding: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${({ isActive, theme }) =>
    isActive ? theme.dataTable.sortIconActiveColor : theme.dataTable.sortIconColor};
  cursor: pointer;
  border-radius: 2px;
  z-index: 1;

  &:hover {
    background-color: ${({ theme }) => theme.dataTable.rowHoverBackgroundColor};
    color: ${({ theme }) => theme.dataTable.sortIconActiveColor};
  }

  &:focus {
    outline: none;
  }
`;

export const RowCountText = styled.span`
  ${crFont11}
  margin: 10px;
  display: inline-block;
  color: ${({ theme }) => theme.dataTable.rowCountTextColor};
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 30px;
  color: ${({ theme }) => theme.dataTable.rowTextColor};
  font-size: 11px;
`;

export const LoadingOverlay = styled.div<{ hasTitle?: boolean }>`
  position: absolute;
  top: ${({ hasTitle }) => (hasTitle ? '101px' : '50px')};
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: ${({ theme }) => theme.dataTable.backgroundColor}CC;
  color: ${({ theme }) => theme.dataTable.rowTextColor};
  font-size: 14px;
  z-index: 100;
`;

export const DragHandle = styled.div`
  position: absolute;
  left: 4px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  cursor: grab;
  color: ${({ theme }) => theme.dataTable.sortIconColor};
  opacity: 0.5;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  &:active {
    cursor: grabbing;
  }
`;

export const DownloadButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  color: ${({ theme }) => theme.dataTable.headerTextColor};
  opacity: 0.7;
  transition: opacity 0.15s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    opacity: 1;
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

export const DropIndicator = styled.div<{ position: 'left' | 'right' }>`
  position: absolute;
  ${({ position }) => (position === 'left' ? 'left: 0' : 'right: 0')};
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: ${({ theme }) => theme.dataTable.rowSelectedBorderColor};
  z-index: 20;
  pointer-events: none;
`;
